import { useState, useEffect } from 'react';
import { customersApi } from '../lib/api';
import { formatDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogFooter
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { Plus, MagnifyingGlass, Users, Pencil } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function CustomersPage() {
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingCustomer, setEditingCustomer] = useState(null);
    const [formData, setFormData] = useState({
        nome: '',
        cognome: '',
        ragione_sociale: '',
        codice_fiscale: '',
        partita_iva: '',
        indirizzo: '',
        citta: '',
        cap: '',
        provincia: '',
        telefono: '',
        email: '',
        tipo: 'privato'
    });

    useEffect(() => {
        loadCustomers();
    }, []);

    const loadCustomers = async () => {
        try {
            const response = await customersApi.getAll();
            setCustomers(response.data);
        } catch (error) {
            console.error('Error loading customers:', error);
            toast.error('Errore nel caricamento dei clienti');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            nome: '',
            cognome: '',
            ragione_sociale: '',
            codice_fiscale: '',
            partita_iva: '',
            indirizzo: '',
            citta: '',
            cap: '',
            provincia: '',
            telefono: '',
            email: '',
            tipo: 'privato'
        });
        setEditingCustomer(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingCustomer) {
                await customersApi.update(editingCustomer.id, formData);
                toast.success('Cliente aggiornato');
            } else {
                await customersApi.create(formData);
                toast.success('Cliente creato');
            }
            setDialogOpen(false);
            resetForm();
            loadCustomers();
        } catch (error) {
            console.error('Error saving customer:', error);
            // Extract error message from response
            const errorMessage = error.response?.data?.detail || 
                                 error.response?.data?.message ||
                                 error.message ||
                                 'Errore nel salvataggio del cliente';
            toast.error(typeof errorMessage === 'string' ? errorMessage : 'Errore nel salvataggio del cliente');
        }
    };

    const handleEdit = (customer) => {
        setEditingCustomer(customer);
        setFormData({
            nome: customer.nome || '',
            cognome: customer.cognome || '',
            ragione_sociale: customer.ragione_sociale || '',
            codice_fiscale: customer.codice_fiscale || '',
            partita_iva: customer.partita_iva || '',
            indirizzo: customer.indirizzo || '',
            citta: customer.citta || '',
            cap: customer.cap || '',
            provincia: customer.provincia || '',
            telefono: customer.telefono || '',
            email: customer.email || '',
            tipo: customer.tipo || 'privato'
        });
        setDialogOpen(true);
    };

    const filteredCustomers = customers.filter(c => {
        if (!search) return true;
        const searchLower = search.toLowerCase();
        return (
            c.nome?.toLowerCase().includes(searchLower) ||
            c.cognome?.toLowerCase().includes(searchLower) ||
            c.ragione_sociale?.toLowerCase().includes(searchLower) ||
            c.email?.toLowerCase().includes(searchLower) ||
            c.telefono?.includes(search)
        );
    });

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="customers-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row gap-4 justify-between">
                <div className="relative flex-1 max-w-md">
                    <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                        placeholder="Cerca clienti..."
                        data-testid="customers-search-input"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-9"
                    />
                </div>
                <Dialog open={dialogOpen} onOpenChange={(open) => {
                    setDialogOpen(open);
                    if (!open) resetForm();
                }}>
                    <DialogTrigger asChild>
                        <Button data-testid="create-customer-button">
                            <Plus className="w-4 h-4 mr-2" />
                            Nuovo Cliente
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="font-heading">
                                {editingCustomer ? 'Modifica Cliente' : 'Nuovo Cliente'}
                            </DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="space-y-2">
                                <Label>Tipo Cliente</Label>
                                <Select 
                                    value={formData.tipo} 
                                    onValueChange={(v) => setFormData({...formData, tipo: v})}
                                >
                                    <SelectTrigger data-testid="customer-type-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="privato">Privato</SelectItem>
                                        <SelectItem value="azienda">Azienda</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            {formData.tipo === 'privato' ? (
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Nome *</Label>
                                        <Input
                                            data-testid="customer-name-input"
                                            value={formData.nome}
                                            onChange={(e) => setFormData({...formData, nome: e.target.value})}
                                            required
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Cognome</Label>
                                        <Input
                                            value={formData.cognome}
                                            onChange={(e) => setFormData({...formData, cognome: e.target.value})}
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    <Label>Ragione Sociale *</Label>
                                    <Input
                                        data-testid="customer-company-input"
                                        value={formData.ragione_sociale}
                                        onChange={(e) => setFormData({...formData, ragione_sociale: e.target.value, nome: e.target.value})}
                                        required
                                    />
                                </div>
                            )}

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Codice Fiscale</Label>
                                    <Input
                                        value={formData.codice_fiscale}
                                        onChange={(e) => setFormData({...formData, codice_fiscale: e.target.value.toUpperCase()})}
                                        maxLength={16}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Partita IVA</Label>
                                    <Input
                                        value={formData.partita_iva}
                                        onChange={(e) => setFormData({...formData, partita_iva: e.target.value})}
                                        maxLength={11}
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label>Indirizzo</Label>
                                <Input
                                    value={formData.indirizzo}
                                    onChange={(e) => setFormData({...formData, indirizzo: e.target.value})}
                                    placeholder="Via Roma 1"
                                />
                            </div>

                            <div className="grid grid-cols-3 gap-4">
                                <div className="space-y-2">
                                    <Label>Città</Label>
                                    <Input
                                        value={formData.citta}
                                        onChange={(e) => setFormData({...formData, citta: e.target.value})}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>CAP</Label>
                                    <Input
                                        value={formData.cap}
                                        onChange={(e) => setFormData({...formData, cap: e.target.value})}
                                        maxLength={5}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Provincia</Label>
                                    <Input
                                        value={formData.provincia}
                                        onChange={(e) => setFormData({...formData, provincia: e.target.value.toUpperCase()})}
                                        maxLength={2}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Telefono</Label>
                                    <Input
                                        value={formData.telefono}
                                        onChange={(e) => setFormData({...formData, telefono: e.target.value})}
                                        placeholder="+39 02 1234567"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Email</Label>
                                    <Input
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                                        placeholder="email@esempio.it"
                                    />
                                </div>
                            </div>

                            <DialogFooter>
                                <Button type="button" variant="outline" onClick={() => {
                                    setDialogOpen(false);
                                    resetForm();
                                }}>
                                    Annulla
                                </Button>
                                <Button type="submit" data-testid="submit-customer-button">
                                    {editingCustomer ? 'Salva Modifiche' : 'Crea Cliente'}
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            {/* Customers Table */}
            <Card>
                <CardContent className="p-0">
                    <Table>
                        <TableHeader>
                            <TableRow className="table-header">
                                <TableHead>Cliente</TableHead>
                                <TableHead>Tipo</TableHead>
                                <TableHead>Contatti</TableHead>
                                <TableHead>Località</TableHead>
                                <TableHead>Data</TableHead>
                                <TableHead className="w-12"></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredCustomers.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} className="text-center py-12">
                                        <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
                                        <p className="text-muted-foreground">Nessun cliente trovato</p>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredCustomers.map((customer) => (
                                    <TableRow 
                                        key={customer.id} 
                                        className="table-row"
                                        data-testid={`customer-row-${customer.id}`}
                                    >
                                        <TableCell className="table-cell">
                                            <div>
                                                <p className="font-medium">
                                                    {customer.tipo === 'azienda' 
                                                        ? customer.ragione_sociale 
                                                        : `${customer.nome} ${customer.cognome || ''}`}
                                                </p>
                                                {customer.codice_fiscale && (
                                                    <p className="text-xs text-muted-foreground">
                                                        CF: {customer.codice_fiscale}
                                                    </p>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell className="table-cell">
                                            <Badge variant="outline">
                                                {customer.tipo === 'azienda' ? 'Azienda' : 'Privato'}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="table-cell">
                                            <div className="text-sm">
                                                {customer.email && <p>{customer.email}</p>}
                                                {customer.telefono && <p className="text-muted-foreground">{customer.telefono}</p>}
                                            </div>
                                        </TableCell>
                                        <TableCell className="table-cell text-muted-foreground">
                                            {[customer.citta, customer.provincia].filter(Boolean).join(' ') || '-'}
                                        </TableCell>
                                        <TableCell className="table-cell text-muted-foreground">
                                            {formatDate(customer.created_at)}
                                        </TableCell>
                                        <TableCell className="table-cell">
                                            <Button 
                                                variant="ghost" 
                                                size="sm"
                                                onClick={() => handleEdit(customer)}
                                            >
                                                <Pencil className="w-4 h-4" />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        </div>
    );
}
